import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus,
  FiLayers,
  FiSearch,
  FiTrash2,
  FiX,
  FiBook,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiBarChart2,
  FiKey,
  FiCopy,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, Page, PageHeader, Card, Tag, Button, ProgressBar, Initials, EmptyState, Field, cardStyle, inputStyle, labelStyle, fadeUp } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/classroom.jpg', pos: 'center 30%', height: 640, opacity: 0.6 };
const LINE = 'rgba(249,240,231,0.12)';

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-US', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function parseBatchLines(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      if (line.includes('\t')) {
        const [a, b] = line.split('\t');
        if (a && b) return { displayName: a.trim(), accessCode: b.trim() };
      }
      const idx = line.indexOf(',');
      if (idx === -1) return null;
      const displayName = line.slice(0, idx).trim();
      const accessCode = line.slice(idx + 1).trim();
      if (!displayName || !accessCode) return null;
      return { displayName, accessCode };
    })
    .filter(Boolean);
}

const ACCESS_CODE_HELP = 'Access code must be 4-20 letters/numbers.';
const ACCESS_CODE_RE = /^[a-zA-Z0-9]{4,20}$/;

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(7, 20, 45, 0.6)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1000,
  fontFamily: BRAND.font,
};

// Dark "glass" dialog, same surface as the page cards.
const modalStyle = {
  ...cardStyle(false),
  width: '100%',
  maxWidth: 520,
  maxHeight: '90vh',
  overflow: 'auto',
  background: 'rgba(11,26,58,0.92)',
  boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
  padding: 26,
};

const input = inputStyle(false);
const helpStyle = { marginTop: 6, fontSize: '0.78rem', fontWeight: 600, color: BRAND.muted };

function AdminLoading({ message }) {
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <LoadingSpinner message={message} />
    </Card>
  );
}

function ModalHeader({ kicker, title, onClose, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
      <div style={{ minWidth: 0 }}>
        {kicker && <div style={{ fontWeight: 800, letterSpacing: '0.24em', fontSize: '0.7rem', textTransform: 'uppercase', color: BRAND.goldLight }}>{kicker}</div>}
        <h2 style={{ fontSize: '1.35rem', fontWeight: 900, color: BRAND.ivory, margin: '4px 0 0', lineHeight: 1.15 }}>{title}</h2>
        {children}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{ width: 36, height: 36, borderRadius: '50%', border: `1px solid ${LINE}`, background: 'rgba(249,240,231,0.08)', color: BRAND.ivory, display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0 }}
      >
        <FiX size={18} />
      </button>
    </div>
  );
}

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseFilter, setCourseFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [progressStudent, setProgressStudent] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  const [formName, setFormName] = useState('');
  const [formCode, setFormCode] = useState('');
  const [formCourse, setFormCourse] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [batchText, setBatchText] = useState('');
  const [batchCourse, setBatchCourse] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  // Reset access code: { student, code (null until the API answers), custom (teacher-typed code), busy }
  const [resetState, setResetState] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (courseFilter) params.course = courseFilter;
      if (searchDebounced) params.search = searchDebounced;

      const [stuRes, courseRes] = await Promise.all([
        api.get('/admin/students', { params }),
        api.get('/courses'),
      ]);

      setStudents(stuRes.data.students || []);
      const raw = courseRes.data.courses || courseRes.data || [];
      setCourses(Array.isArray(raw) ? raw : []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error loading');
      setStudents([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [courseFilter, searchDebounced]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const openProgress = async (student) => {
    setProgressStudent(student);
    setProgressData(null);
    setProgressLoading(true);
    try {
      const { data } = await api.get(`/admin/students/${student._id}/progress`);
      setProgressData(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Unable to load progress');
      setProgressStudent(null);
    } finally {
      setProgressLoading(false);
    }
  };

  const closeProgress = () => {
    setProgressStudent(null);
    setProgressData(null);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!formName.trim() || !formCode.trim() || !formCourse) {
      toast.error('Fill in all fields');
      return;
    }
    if (!ACCESS_CODE_RE.test(formCode.trim())) {
      toast.error(ACCESS_CODE_HELP);
      return;
    }
    setFormSubmitting(true);
    try {
      await api.post('/admin/students', {
        displayName: formName.trim(),
        accessCode: formCode.trim(),
        courseId: formCourse,
      });
      toast.success('Student created!');
      setAddOpen(false);
      setFormName('');
      setFormCode('');
      setFormCourse('');
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Creation failed');
    } finally {
      setFormSubmitting(false);
    }
  };

  const submitBatch = async (e) => {
    e.preventDefault();
    const parsed = parseBatchLines(batchText);
    if (!parsed.length || !batchCourse) {
      toast.error('Enter valid rows (name, code) and choose a course');
      return;
    }
    const invalidRow = parsed.find((row) => !ACCESS_CODE_RE.test(row.accessCode));
    if (invalidRow) {
      toast.error(`${invalidRow.displayName}: ${ACCESS_CODE_HELP}`);
      return;
    }
    setBatchSubmitting(true);
    try {
      const { data } = await api.post('/admin/students/batch', {
        students: parsed,
        courseId: batchCourse,
      });
      const ok = (data.results || []).filter((r) => r.success).length;
      const fail = (data.results || []).length - ok;
      if (ok) toast.success(`${ok} students created`);
      if (fail) toast.error(`${fail} rows failed`);
      setBatchOpen(false);
      setBatchText('');
      setBatchCourse('');
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setBatchSubmitting(false);
    }
  };

  const deactivateStudent = async (student) => {
    if (!window.confirm(`Deactivate ${student.displayName}?`)) return;
    try {
      await api.delete(`/admin/students/${student._id}`);
      toast.success('Student deactivated');
      fetchLists();
      if (progressStudent?._id === student._id) closeProgress();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
  };

  const openReset = (student) => setResetState({ student, code: null, custom: '', busy: false });
  const closeReset = () => setResetState(null);
  const submitReset = async (e) => {
    e?.preventDefault?.();
    if (!resetState || resetState.busy) return;
    setResetState((r) => ({ ...r, busy: true }));
    try {
      const body = resetState.custom.trim() ? { accessCode: resetState.custom.trim() } : {};
      const { data } = await api.put(`/admin/students/${resetState.student._id}/access-code`, body);
      setResetState((r) => ({ ...r, code: data.accessCode, busy: false }));
      toast.success('New access code set');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not reset the code');
      setResetState((r) => (r ? { ...r, busy: false } : r));
    }
  };
  const copyCode = async () => {
    try { await navigator.clipboard.writeText(resetState.code); toast.success('Copied'); } catch { toast.error('Copy failed — write it down'); }
  };

  const courseTitleById = useMemo(() => {
    const m = {};
    courses.forEach((c) => {
      m[c._id] = c.title || c.name || 'Course';
    });
    return m;
  }, [courses]);

  const progressLessons = progressData?.progress?.lessons || [];
  const lessonsDone = progressLessons.filter((lp) => lp.completed).length;
  const lessonsTotal = progressData?.totalLessons || progressLessons.length;
  const lessonsPct = lessonsTotal ? Math.round((lessonsDone / lessonsTotal) * 100) : 0;
  // One row per quiz: best percentage, number of attempts, passed if any attempt passed.
  const progressQuizzes = useMemo(() => {
    const byQuiz = new Map();
    for (const qa of progressData?.progress?.quizAttempts || []) {
      const key = qa.quiz?._id || String(qa.quiz || '');
      const cur = byQuiz.get(key);
      if (!cur) byQuiz.set(key, { key, title: qa.quiz?.title || 'Quiz', order: qa.quiz?.order ?? 999, best: qa.percentage || 0, attempts: 1, passed: !!qa.passed });
      else { cur.best = Math.max(cur.best, qa.percentage || 0); cur.attempts += 1; cur.passed = cur.passed || !!qa.passed; }
    }
    return [...byQuiz.values()].sort((a, b) => a.order - b.order);
  }, [progressData]);
  const progressGames = useMemo(() => {
    const titles = new Map((progressData?.games || []).map((g) => [g._id, g.title]));
    return (progressData?.progress?.gameProgress || []).map((g) => ({ ...g, title: titles.get(g.gameId) || 'Game' }));
  }, [progressData]);
  const progressTotals = {
    points: progressData?.progress?.totalPoints || 0,
    badges: (progressData?.progress?.badges || []).length,
    gamesDone: progressGames.filter((g) => g.completed).length,
    gamesTotal: (progressData?.games || []).length,
  };

  return (
    <Page hero={HERO}>
      <style>{`
        .core-students-table { min-width: 680px; }
        .core-students-filters { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
        .core-students-filters .core-search { position: relative; flex: 1 1 220px; min-width: 200px; }
        .core-students-filters select { flex: 0 1 220px; }
        @media (max-width: 640px) {
          .core-students-filters { width: 100%; }
          .core-students-filters select { flex: 1 1 100%; }
          .core-students-hide-sm { display: none !important; }
        }
      `}</style>

      <PageHeader
        kicker="Administration"
        title="Students"
        subtitle={loading ? 'Loading the class list…' : `${students.length} ${students.length === 1 ? 'student' : 'students'} in view. Click a row to see the progress.`}
        right={
          <div className="core-students-filters">
            <div className="core-search">
              <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: BRAND.muted, pointerEvents: 'none' }} />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name…"
                style={{ ...input, paddingLeft: 40, borderRadius: 999, padding: '10px 14px 10px 40px' }}
              />
            </div>
            <select
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              style={{ ...input, borderRadius: 999, padding: '10px 14px', cursor: 'pointer', fontWeight: 700, width: 'auto' }}
            >
              <option value="">All courses</option>
              {courses.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.title}
                </option>
              ))}
            </select>
            <Button variant="gold" size="sm" onClick={() => setAddOpen(true)}>
              <FiPlus size={16} /> Add student
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBatchOpen(true)}>
              <FiLayers size={16} /> Add group
            </Button>
          </div>
        }
      />

      {loading ? (
        <AdminLoading message="Loading the students…" />
      ) : students.length === 0 ? (
        <EmptyState
          icon="🎒"
          title="No students found"
          text={searchDebounced || courseFilter ? 'Try a different name or course filter.' : 'Add your first student or import a whole class at once.'}
          action={<Button variant="gold" onClick={() => setAddOpen(true)}><FiPlus size={16} /> Add student</Button>}
        />
      ) : (
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="core-table core-students-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th className="core-students-hide-sm">Last login</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, i) => (
                    <motion.tr
                      key={s._id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(i, 20) * 0.03 }}
                      onClick={() => openProgress(s)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Initials name={s.displayName} size={36} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 800, color: BRAND.ivory, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.displayName}</div>
                            {s.accessCode && <div style={{ fontSize: '0.74rem', color: BRAND.muted, fontWeight: 600, letterSpacing: '0.04em' }}>{s.accessCode}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Tag tone="gold">{s.course?.title || courseTitleById[s.course] || '—'}</Tag>
                      </td>
                      <td className="core-students-hide-sm" style={{ color: BRAND.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatDate(s.lastLogin)}
                      </td>
                      <td>
                        <Tag tone={s.isActive ? 'success' : 'danger'}>
                          {s.isActive ? <FiCheckCircle size={12} /> : <FiAlertCircle size={12} />}
                          {s.isActive ? 'Active' : 'Inactive'}
                        </Tag>
                      </td>
                      <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <Button variant="ghost" size="sm" onClick={() => openProgress(s)}>
                            <FiBarChart2 size={14} /> Progress
                          </Button>
                          {s.isActive && (
                            <Button variant="ghost" size="sm" onClick={() => openReset(s)} title="Give the student a new access code">
                              <FiKey size={14} /> Reset code
                            </Button>
                          )}
                          <Button variant="danger" size="sm" onClick={() => deactivateStudent(s)}>
                            <FiTrash2 size={14} /> Deactivate
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>
      )}

      <AnimatePresence>
        {resetState && (
          <motion.div style={overlayStyle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeReset}>
            <motion.div role="dialog" aria-modal="true" initial={{ scale: 0.94, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0, y: 10 }} style={modalStyle} onClick={(e) => e.stopPropagation()}>
              <ModalHeader kicker="Students" title={`Reset code — ${resetState.student.displayName}`} onClose={closeReset} />
              {resetState.code ? (
                <div>
                  <p style={{ ...helpStyle, marginTop: 0 }}>Here is the new access code. It is shown only once — give it to the student now. Progress, points and badges are kept.</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 18px', padding: '14px 16px', borderRadius: 14, background: 'rgba(226,184,120,0.12)', border: '1px solid rgba(226,184,120,0.4)' }}>
                    <code style={{ flex: 1, fontSize: '1.5rem', fontWeight: 900, letterSpacing: '0.08em', color: BRAND.goldLight, fontFamily: 'inherit', userSelect: 'all' }}>{resetState.code}</code>
                    <Button variant="ghost" size="sm" onClick={copyCode}><FiCopy size={14} /> Copy</Button>
                  </div>
                  <Button variant="gold" size="lg" onClick={closeReset} style={{ width: '100%' }}>Done</Button>
                </div>
              ) : (
                <form onSubmit={submitReset}>
                  <p style={{ ...helpStyle, marginTop: 0 }}>Access codes are stored encrypted and cannot be shown back. Set a new one: the old code stops working and any open session of the student is signed out.</p>
                  <Field label="New access code (optional)">
                    <input value={resetState.custom} onChange={(e) => setResetState((r) => ({ ...r, custom: e.target.value }))} style={input} placeholder="Leave empty to generate one (e.g. owl4821)" minLength={4} maxLength={20} pattern="[A-Za-z0-9]{4,20}" />
                    <p style={helpStyle}>{ACCESS_CODE_HELP}</p>
                  </Field>
                  <Button variant="gold" size="lg" type="submit" disabled={resetState.busy} style={{ width: '100%' }}>
                    {resetState.busy ? 'Saving…' : resetState.custom.trim() ? 'Set this code' : 'Generate new code'}
                  </Button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
        {addOpen && (
          <motion.div
            style={overlayStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setAddOpen(false)}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ scale: 0.94, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 10 }}
              style={modalStyle}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader kicker="Students" title="New student" onClose={() => setAddOpen(false)} />
              <form onSubmit={submitAdd}>
                <Field label="Display name">
                  <input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    style={input}
                    placeholder="e.g. John D."
                  />
                </Field>
                <Field label="Access code">
                  <input
                    value={formCode}
                    onChange={(e) => setFormCode(e.target.value)}
                    style={input}
                    placeholder="At least 4 letters/numbers"
                    minLength={4}
                    maxLength={20}
                    pattern="[A-Za-z0-9]{4,20}"
                  />
                  <p style={helpStyle}>{ACCESS_CODE_HELP}</p>
                </Field>
                <Field label="Course" style={{ marginBottom: 20 }}>
                  <select
                    value={formCourse}
                    onChange={(e) => setFormCourse(e.target.value)}
                    style={{ ...input, cursor: 'pointer' }}
                  >
                    <option value="">Select course…</option>
                    {courses.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                </Field>
                <Button variant="gold" size="lg" type="submit" disabled={formSubmitting} style={{ width: '100%' }}>
                  {formSubmitting ? 'Saving…' : 'Create student'}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {batchOpen && (
          <motion.div
            style={overlayStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setBatchOpen(false)}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ scale: 0.94, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 10 }}
              style={{ ...modalStyle, maxWidth: 560 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader kicker="Students" title="Add a group" onClose={() => setBatchOpen(false)} />
              <p style={{ marginBottom: 14, color: BRAND.muted, fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.5 }}>
                One line per student: <strong style={{ color: BRAND.ivory }}>name, code</strong> or separated by tab. Codes must be 4-20 letters/numbers.
              </p>
              <form onSubmit={submitBatch}>
                <Field label="Students">
                  <textarea
                    value={batchText}
                    onChange={(e) => setBatchText(e.target.value)}
                    rows={8}
                    placeholder={'John Smith, ABC123\nJane Doe, XYZ789'}
                    style={{ ...input, resize: 'vertical', minHeight: 160, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.88rem' }}
                  />
                </Field>
                <Field label="Course" style={{ marginBottom: 20 }}>
                  <select
                    value={batchCourse}
                    onChange={(e) => setBatchCourse(e.target.value)}
                    style={{ ...input, cursor: 'pointer' }}
                  >
                    <option value="">Select course…</option>
                    {courses.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                </Field>
                <Button variant="gold" size="lg" type="submit" disabled={batchSubmitting} style={{ width: '100%' }}>
                  {batchSubmitting ? 'Importing…' : 'Create group'}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {progressStudent && (
          <motion.div
            style={overlayStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeProgress}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ scale: 0.94, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 10 }}
              style={{ ...modalStyle, maxWidth: 640 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader kicker="Student progress" title={progressStudent.displayName} onClose={closeProgress}>
                <p style={{ marginTop: 6, fontWeight: 700, color: BRAND.muted, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}>
                  <FiBook size={15} style={{ color: BRAND.gold }} />
                  {progressStudent.course?.title || courseTitleById[progressStudent.course] || '—'}
                </p>
              </ModalHeader>

              {progressLoading && (
                <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.15rem', color: BRAND.muted, margin: '8px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src={BRAND.owl} alt="" style={{ width: 26, height: 26 }} /> Loading progress…
                </p>
              )}

              {!progressLoading && progressData && (
                <div style={{ display: 'grid', gap: 22 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Initials name={progressStudent.displayName} size={44} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.8rem', fontWeight: 800, color: BRAND.muted, marginBottom: 6 }}>
                        <span>Lessons completed</span>
                        <span style={{ color: BRAND.ivory }}>{lessonsDone}/{lessonsTotal} · {lessonsPct}%</span>
                      </div>
                      <ProgressBar value={lessonsPct} height={6} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {[
                      ['Points', `${progressTotals.points}`],
                      ['Badges', `${progressTotals.badges}`],
                      ['Games', `${progressTotals.gamesDone}/${progressTotals.gamesTotal}`],
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: '10px 12px', borderRadius: 12, background: 'rgba(249,240,231,0.05)', border: `1px solid ${LINE}`, textAlign: 'center' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: 900, color: BRAND.goldLight, lineHeight: 1.1 }}>{value}</div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: BRAND.muted, marginTop: 3 }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h3 style={labelStyle()}>Lessons</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
                      {progressLessons.length === 0 && (
                        <p style={{ color: BRAND.muted, fontWeight: 600, fontSize: '0.9rem' }}>No lessons recorded.</p>
                      )}
                      {progressLessons.map((lp, idx) => (
                        <div
                          key={lp.lesson?._id || idx}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(249,240,231,0.05)', border: `1px solid ${LINE}` }}
                        >
                          <span style={{ fontWeight: 800, color: BRAND.ivory, fontSize: '0.92rem' }}>{lp.lesson?.title || 'Lesson'}</span>
                          <Tag tone={lp.completed ? 'success' : 'gold'}>{lp.completed ? 'Completed' : 'In progress'}</Tag>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 style={labelStyle()}>Quiz</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
                      {progressQuizzes.length === 0 && (
                        <p style={{ color: BRAND.muted, fontWeight: 600, fontSize: '0.9rem' }}>No quizzes completed.</p>
                      )}
                      {progressQuizzes.map((qa) => (
                        <div
                          key={qa.key}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(249,240,231,0.05)', border: `1px solid ${LINE}` }}
                        >
                          <span style={{ fontWeight: 800, color: BRAND.ivory, fontSize: '0.92rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{qa.title}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: BRAND.muted, fontSize: '0.9rem', flexShrink: 0 }}>
                            {qa.attempts > 1 && <span style={{ fontSize: '0.72rem', opacity: 0.8 }}>{qa.attempts} tries</span>}
                            <FiClock size={15} style={{ color: BRAND.gold }} />
                            {`best ${Math.round(qa.best)}%`}
                            {qa.passed ? (
                              <FiCheckCircle style={{ color: '#8fd9ad' }} size={17} />
                            ) : (
                              <FiAlertCircle style={{ color: BRAND.gold }} size={17} />
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {progressGames.length > 0 && (
                    <div>
                      <h3 style={labelStyle()}>Games</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {progressGames.map((g) => (
                          <div key={g.gameId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(249,240,231,0.05)', border: `1px solid ${LINE}` }}>
                            <span style={{ fontWeight: 800, color: BRAND.ivory, fontSize: '0.92rem' }}>{g.title}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: BRAND.muted, fontSize: '0.9rem' }}>
                              {g.score || 0} pts · level {g.level || 1}
                              {g.completed ? <FiCheckCircle style={{ color: '#8fd9ad' }} size={17} /> : <Tag tone="gold">Playing</Tag>}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Page>
  );
}
